import { io, type Socket } from 'socket.io-client';
import type { SocketConnectionStatus } from '@/types/notification';
import { getSocketServerUrl } from '@/utils/socket-url';

type StatusListener = (status: SocketConnectionStatus) => void;

class SocketConnectionService {
    private socket: Socket | null = null;

    private refCount = 0;

    private status: SocketConnectionStatus = 'disconnected';

    private statusListeners = new Set<StatusListener>();

    subscribe(listener: StatusListener): () => void {
        this.statusListeners.add(listener);
        listener(this.status);
        return () => {
            this.statusListeners.delete(listener);
        };
    }

    private setStatus(status: SocketConnectionStatus): void {
        this.status = status;
        this.statusListeners.forEach((listener) => listener(status));
    }

    getSocket(): Socket | null {
        return this.socket;
    }

    getStatus(): SocketConnectionStatus {
        return this.status;
    }

    connect(accessToken: string): void {
        this.refCount += 1;

        if (this.socket) {
            this.socket.auth = { token: accessToken };
            if (!this.socket.connected) {
                this.socket.connect();
            }
            return;
        }

        this.setStatus('connecting');

        const socket = io(getSocketServerUrl(), {
            auth: { token: accessToken },
            transports: ['websocket'],
            autoConnect: true
        });

        this.socket = socket;

        socket.on('connect', () => {
            this.setStatus('connected');
        });

        socket.on('disconnect', () => {
            this.setStatus('disconnected');
        });

        socket.on('connect_error', () => {
            this.setStatus('error');
        });
    }

    updateToken(accessToken: string): void {
        if (!this.socket) {
            return;
        }
        this.socket.auth = { token: accessToken };
        if (!this.socket.connected) {
            this.socket.connect();
        }
    }

    disconnect(): void {
        this.refCount = Math.max(0, this.refCount - 1);

        if (this.refCount > 0) {
            return;
        }

        this.socket?.removeAllListeners();
        this.socket?.disconnect();
        this.socket = null;
        this.setStatus('disconnected');
    }
}

export default new SocketConnectionService();
